import { useState } from "react";
import { FiStar } from "react-icons/fi";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import useMerchantReviews from "../../hooks/useMerchantReviews";
import useMerchantReviewReply from "../hooks/useMerchantReviewReply";
import StarRating from "../../../Routes/Marketplace/components/StarRating";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";

const MerchantReviewsPage = () => {
  const { data, isLoading, isError } = useMerchantReviews();
  const { mutate: postReply, isLoading: replying } = useMerchantReviewReply();
  const reviews = data?.reviews || [];
  const [replyDrafts, setReplyDrafts] = useState({});
  const [openReplyId, setOpenReplyId] = useState(null);

  const handleReply = (reviewId) => {
    const reply = (replyDrafts[reviewId] || "").trim();
    if (!reply) {
      toast.error("Enter a reply");
      return;
    }
    postReply(
      { reviewId, reply },
      {
        onSuccess: () => {
          toast.success("Reply posted");
          setOpenReplyId(null);
          setReplyDrafts((prev) => ({ ...prev, [reviewId]: "" }));
        },
        onError: (err) => toast.error(err.response?.data?.message || "Could not post reply"),
      }
    );
  };

  return (
    <>
      <PageHeader title="Reviews" subtitle="Customer feedback and your public replies" icon={FiStar} />
      {isError && <p style={{ color: "var(--dash-danger)" }}>Failed to load reviews.</p>}
      {isLoading && <p>Loading reviews…</p>}
      {!isLoading && reviews.length === 0 && !isError && (
        <EmptyState icon={FiStar} title="No reviews yet" message="Reviews from customers will appear here." />
      )}
      <div className="dashboard-review-list">
        {reviews.map((review) => (
          <article key={review._id} className="dashboard-card dashboard-review-card">
            <div className="dashboard-review-card__header">
              <div>
                <strong>
                  {review.user?.first_name} {review.user?.last_name}
                </strong>
                <StarRating rating={review.rating} showCount={false} size="sm" />
              </div>
              <small>{new Date(review.createdAt).toLocaleDateString()}</small>
            </div>
            {review.product?.slug ? (
              <Link to={`/merchant/products/${review.product.slug}`} className="dashboard-link">
                {review.product.name}
              </Link>
            ) : (
              <p className="dashboard-form-hint">{review.product?.name || "Product"}</p>
            )}
            <p className="dashboard-review-card__comment">{review.comment || "No comment"}</p>
            {review.merchantReply && (
              <div className="dashboard-review-reply">
                <strong>Your reply</strong>
                <p>{review.merchantReply}</p>
                {review.merchantReplyAt && (
                  <small>{new Date(review.merchantReplyAt).toLocaleString()}</small>
                )}
              </div>
            )}
            {openReplyId === review._id ? (
              <div className="dashboard-review-reply-form">
                <textarea
                  rows={3}
                  value={replyDrafts[review._id] ?? review.merchantReply ?? ""}
                  onChange={(e) =>
                    setReplyDrafts((prev) => ({ ...prev, [review._id]: e.target.value }))
                  }
                  placeholder="Write a public reply to this customer…"
                />
                <div className="dashboard-form-actions">
                  <button
                    type="button"
                    className="dashboard-btn dashboard-btn--primary"
                    disabled={replying}
                    onClick={() => handleReply(review._id)}
                  >
                    {replying ? "Posting…" : review.merchantReply ? "Update reply" : "Post reply"}
                  </button>
                  <button
                    type="button"
                    className="dashboard-btn dashboard-btn--secondary"
                    onClick={() => setOpenReplyId(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="dashboard-btn dashboard-btn--secondary"
                onClick={() => setOpenReplyId(review._id)}
              >
                {review.merchantReply ? "Edit reply" : "Reply"}
              </button>
            )}
          </article>
        ))}
      </div>
    </>
  );
};

export default MerchantReviewsPage;
